import { useRef, useState, useCallback, useEffect } from "react";

// A soft, self-contained ambient pad synthesized with the Web Audio API.
// Returns a level ref (0..1) that gently breathes while playing so the
// particle system can pulse in sync. No external audio file needed.
//
// NOTE: the master-gain envelope is driven purely by direct `.value`
// assignment inside a requestAnimationFrame loop — we deliberately avoid
// AudioParam scheduling methods (setTargetAtValue / linearRampToValueAtTime)
// for maximum cross-browser compatibility.
export default function useAmbientAudio() {
  const ctxRef = useRef(null);
  const nodesRef = useRef(null);
  const rafRef = useRef(null);
  const levelRef = useRef(0);
  const fadeRef = useRef(0); // current envelope 0..1
  const fadeTargetRef = useRef(0); // target envelope 0..1
  const enabledRef = useRef(false);
  const [enabled, setEnabled] = useState(false);

  const stopLoop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  const disposeNodes = useCallback(() => {
    const n = nodesRef.current;
    if (n) {
      n.oscs.forEach((o) => {
        try { o.stop(); } catch (e) {}
        try { o.disconnect(); } catch (e) {}
      });
      try { n.master.disconnect(); } catch (e) {}
      try { n.lp.disconnect(); } catch (e) {}
    }
    nodesRef.current = null;
    levelRef.current = 0;
  }, []);

  const runLoop = useCallback(() => {
    if (rafRef.current) return; // already running
    const t0 = performance.now();
    const loop = () => {
      const ctx = ctxRef.current;
      const n = nodesRef.current;
      if (!ctx || !n) {
        rafRef.current = null;
        return;
      }
      const t = (performance.now() - t0) / 1000;
      // ease the fade envelope toward its target
      fadeRef.current += (fadeTargetRef.current - fadeRef.current) * 0.045;
      const fade = fadeRef.current;
      // slow breathing 0..1
      const swell = 0.5 + 0.5 * Math.sin(t * 0.5);

      // direct value assignment (no scheduling API)
      try {
        n.master.gain.value = fade * (0.05 + swell * 0.06);
        n.lp.frequency.value = 650 + swell * 500;
      } catch (e) {}

      levelRef.current = fade * swell;

      // finished fading out -> clean up
      if (fadeTargetRef.current === 0 && fade < 0.002) {
        disposeNodes();
        rafRef.current = null;
        return;
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  }, [disposeNodes]);

  const startAudio = useCallback(async () => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!ctxRef.current) ctxRef.current = new AC();
    const ctx = ctxRef.current;
    try {
      if (ctx.state === "suspended") await ctx.resume();
    } catch (e) {}

    // if user toggled off again before resume resolved, bail out
    if (!enabledRef.current) return;
    if (nodesRef.current) {
      // already have a graph (e.g. StrictMode) -> just make sure it's audible
      fadeTargetRef.current = 1;
      runLoop();
      return;
    }

    const master = ctx.createGain();
    master.gain.value = 0.0001;

    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 800;
    lp.Q.value = 0.6;
    lp.connect(master);
    master.connect(ctx.destination);

    // gentle A-major-ish pad
    const freqs = [110, 164.81, 220, 277.18];
    const oscs = freqs.map((f, i) => {
      const o = ctx.createOscillator();
      o.type = i % 2 === 0 ? "sine" : "triangle";
      o.frequency.value = f;
      o.detune.value = (Math.random() - 0.5) * 8;
      const g = ctx.createGain();
      g.gain.value = 0.16 / freqs.length;
      o.connect(g);
      g.connect(lp);
      o.start();
      return o;
    });

    nodesRef.current = { master, oscs, lp };
    fadeRef.current = 0;
    fadeTargetRef.current = 1;
    runLoop();
  }, [runLoop]);

  const stopAudio = useCallback(() => {
    // trigger fade-out; the rAF loop disposes nodes once silent
    fadeTargetRef.current = 0;
    if (!nodesRef.current) {
      levelRef.current = 0;
    }
  }, []);

  const toggle = useCallback(() => {
    const next = !enabledRef.current;
    enabledRef.current = next;
    setEnabled(next);
    if (next) {
      startAudio();
    } else {
      stopAudio();
    }
  }, [startAudio, stopAudio]);

  useEffect(() => {
    return () => {
      stopLoop();
      disposeNodes();
      if (ctxRef.current) {
        try { ctxRef.current.close(); } catch (e) {}
      }
    };
  }, [disposeNodes]);

  return { enabled, toggle, levelRef };
}
